const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

const normalizeColor = (value, fallback) => {
  const candidate = String(value || "").trim();

  return HEX_COLOR_PATTERN.test(candidate)
    ? candidate
    : fallback;
};

const isDefaultColor = (value, fallback) =>
  value.toLowerCase() === fallback.toLowerCase();

const mix = (color, percentage, target) =>
  `color-mix(in srgb, ${color} ${percentage}%, ${target})`;

const PALETTE_DEFAULTS = Object.freeze({
  "pickle-point-nepal": Object.freeze({
    primaryColor: "#FF5A3C",
    accentColor: "#181310",
  }),
  "merlion-gymnasium": Object.freeze({
    primaryColor: "#CC2936",
    accentColor: "#1A1A1A",
  }),
  "bhaisepati-sports-academy": Object.freeze({
    primaryColor: "#FFD700",
    accentColor: "#0A1628",
  }),
  "turf-park": Object.freeze({
    primaryColor: "#2FE5A7",
    accentColor: "#06090F",
  }),
  "boxarena-courtline": Object.freeze({
    primaryColor: "#E7A339",
    accentColor: "#101913",
  }),
});

export const resolveThemePaletteStyle = (
  themeId,
  primaryColor,
  accentColor,
) => {
  const defaults = PALETTE_DEFAULTS[themeId];

  if (!defaults) {
    return {};
  }

  const primary = normalizeColor(
    primaryColor,
    defaults.primaryColor,
  );
  const accent = normalizeColor(
    accentColor,
    defaults.accentColor,
  );

  const usesDefaultPrimary = isDefaultColor(
    primary,
    defaults.primaryColor,
  );
  const usesDefaultAccent = isDefaultColor(
    accent,
    defaults.accentColor,
  );

  switch (themeId) {
    case "pickle-point-nepal":
      return {
        "--pp-coral": primary,
        "--pp-coral-dark": usesDefaultPrimary
          ? "#D94020"
          : mix(primary, 82, "#000000"),
        "--pp-coral-light": usesDefaultPrimary
          ? "rgba(255, 90, 60, 0.1)"
          : mix(primary, 10, "transparent"),
        "--pp-charcoal": accent,
        "--pp-charcoal-mid": usesDefaultAccent
          ? "#2E2926"
          : mix(accent, 86, "#ffffff"),
        "--pp-border": usesDefaultAccent
          ? "rgba(24, 19, 16, 0.09)"
          : mix(accent, 9, "transparent"),
      };

    case "merlion-gymnasium":
      return {
        "--mg-crimson": primary,
        "--mg-crimson-dark": usesDefaultPrimary
          ? "#A31F2B"
          : mix(primary, 82, "#000000"),
        "--mg-crimson-light": usesDefaultPrimary
          ? "rgba(204, 41, 54, 0.08)"
          : mix(primary, 8, "transparent"),
        "--mg-dark": accent,
        "--mg-black": usesDefaultAccent
          ? "#111111"
          : mix(accent, 82, "#000000"),
        "--mg-slate": usesDefaultAccent
          ? "#4A4A5A"
          : mix(accent, 72, "#ffffff"),
        "--mg-border": usesDefaultAccent
          ? "rgba(0, 0, 0, 0.08)"
          : mix(accent, 8, "transparent"),
        "--mg-border-dark": usesDefaultAccent
          ? "rgba(0, 0, 0, 0.15)"
          : mix(accent, 15, "transparent"),
      };

    case "bhaisepati-sports-academy":
      return {
        "--bsa-gold": primary,
        "--bsa-gold-dark": usesDefaultPrimary
          ? "#E5B800"
          : mix(primary, 84, "#000000"),
        "--bsa-gold-light": usesDefaultPrimary
          ? "rgba(255, 215, 0, 0.12)"
          : mix(primary, 12, "transparent"),
        "--bsa-blue-deep": accent,
        "--bsa-blue-mid": usesDefaultAccent
          ? "#162A50"
          : mix(accent, 82, "#ffffff"),
        "--bsa-blue-card": usesDefaultAccent
          ? "#0F1F3D"
          : mix(accent, 90, "#ffffff"),
      };

    case "turf-park":
      return {
        "--tp-green": primary,
        "--tp-green-dark": usesDefaultPrimary
          ? "#059669"
          : mix(primary, 78, "#000000"),
        "--tp-navy": accent,
        "--tp-navy-dark": usesDefaultAccent
          ? "#020407"
          : mix(accent, 82, "#000000"),
        "--tp-navy-card": usesDefaultAccent
          ? "#0F1522"
          : mix(accent, 88, "#ffffff"),
        "--tp-light": accent,
        "--tp-light-card": usesDefaultAccent
          ? "#0F1522"
          : mix(accent, 88, "#ffffff"),
      };

    case "boxarena-courtline":
      return {
        "--bac-amber": primary,
        "--bac-ink": accent,
        "--bac-turf": usesDefaultAccent &&
          usesDefaultPrimary
          ? "#35624A"
          : mix(accent, 78, primary),
        "--bac-turf-deep": usesDefaultAccent
          ? "#1D3327"
          : mix(accent, 85, "#000000"),
      };

    default:
      return {};
  }
};

export { PALETTE_DEFAULTS };
